import { useState, type FC } from "react"
import { Navigate, useNavigate, useParams } from "react-router"
import styled from "styled-components"

import { type EventResponse } from "@forento/shared/models/event"
import { toDateTimeString } from "@forento/shared/utilities/date"
import { parseNumber } from "@forento/shared/utilities/number"

import { type PaypalOrder, PurchaseSuccessModalContextProvider } from "~/contexts/PurchaseSuccessModalContext"
import { useUser } from "~/contexts/UserContext"
import Button from "~/themes/original/components/Button"
import Hero, { CallToActionButtons, CallToActionInfo } from "~/themes/original/components/Hero"
import Layout from "~/themes/original/components/Layout"
import PartialLoadingPage from "~/themes/original/components/PartialLoadingPage"
import PurchaseItem from "~/themes/original/components/PurchaseItem"
import { toRelativeDateString } from "~/themes/original/utilities/date"
import { useTranslation } from "~/translations"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

import AddToCalendar from "./AddToCalendar"

const EventPage: FC = () => {
	const eventId = parseNumber(useParams().eventId ?? "")
	const user = useUser()
	const navigate = useNavigate()
	const t = useTranslation()

	const [paypalOrder, setPaypalOrder] = useState<PaypalOrder | null>(null)

	const { data: event, error, refetch } = query.event.getPersonalized.useQuery(eventId ?? -1)

	if (event === null) return <Navigate to={routes.event.index()} />

	async function handleRespond(response: EventResponse | null) {
		if (eventId === null) return
		if (user.user === null) {
			return navigate(`${routes.user.signup()}?next=${encodeURIComponent(location.pathname)}`)
		}

		await trpc.event.setResponse.mutate({ eventId, response })
		await refetch()
	}

	return (
		<PurchaseSuccessModalContextProvider content={{ type: "event" }} paypalOrder={paypalOrder}>
			<Layout>
				{error ? (
					<p>Failed to load event.</p>
				) : event === undefined ? (
					<PartialLoadingPage />
				) : (
					<Hero
						title={event.title}
						thumbnailUrl={event.thumbnailFilePath}
						htmlDescription={event.longDescription?.html ?? null}
						callToAction={
							<>
								<DateTime>
									{toDateTimeString(event.startDate)} ({toRelativeDateString(event.startDate)})
								</DateTime>
								{!event.isUnlocked ? (
									event.price ? (
										<PurchaseItem
											item={{
												id: event.id,
												name: event.title,
												shortDescription: event.shortDescription,
												price: event.price,
												type: "event",
												accessTimeMonths: null,
											}}
											onPurchase={async ({ paypalOrder }) => {
												setPaypalOrder(paypalOrder)
												await refetch()
											}}
										/>
									) : (
										<>
											<CallToActionInfo>{t("product.onlyForMembers")}</CallToActionInfo>
											<CallToActionButtons>
												<Button variant="primary" onClick={routes.membership.index()}>
													{t("membership.title")}
												</Button>
											</CallToActionButtons>
										</>
									)
								) : event.response === "attending" ? (
									<>
										<CallToActionButtons>
											{event.videoCallLink !== null && (
												<Button variant="primary" onClick={event.videoCallLink} newTab>
													Enter event
												</Button>
											)}
											<Button
												key="cancel"
												variant="danger-bordered"
												onClick={() => handleRespond(null)}
											>
												Cancel attendance
											</Button>
										</CallToActionButtons>
										<AddToCalendar event={event} />
									</>
								) : (
									<CallToActionButtons>
										<Button
											key="attend"
											variant="primary"
											onClick={() => handleRespond("attending")}
										>
											Attend event
										</Button>
										{event.response !== "interested" && (
											<Button
												key="interested"
												variant="secondary"
												onClick={() => handleRespond("interested")}
											>
												I'm interested
											</Button>
										)}
										{event.response !== "not-interested" && (
											<Button
												key="not-interested"
												variant="secondary"
												onClick={() => handleRespond("not-interested")}
											>
												I'm not interested
											</Button>
										)}
									</CallToActionButtons>
								)}
							</>
						}
					/>
				)}
			</Layout>
		</PurchaseSuccessModalContextProvider>
	)
}

const DateTime = styled.time`
	margin-bottom: 16px;
`

export default EventPage
